/**
 * Keys should be snake case of ruby class names
 */
import type { PersistedVariable } from "@dashboardApi/apis/engagementQueryBuilderReport";
import type { BaseTableResultItem, TableResult } from "@tanstack/react-table";

import type { Hour, Minute } from "@braze/beacon-lab-time-picker";

import sharedConstants from "@lib/shared_constants";

export type RefreshFrequency = "Hourly" | "Daily" | "Weekly" | "Monthly" | "None";

export const CRITERIA_OPTIONS = {
  NONE: "",
  CUSTOM_EVENT: sharedConstants.SEGMENT_COHORTS_CRITERIA_OPTIONS.CUSTOM_EVENT,
  PURCHASE: sharedConstants.SEGMENT_COHORTS_CRITERIA_OPTIONS.PURCHASE,
  MESSAGE_CHANNEL_INTERACTION: sharedConstants.SEGMENT_COHORTS_CRITERIA_OPTIONS.MESSAGE_CHANNEL_INTERACTION,
  ECOMMERCE_RECOMMENDED_EVENT: sharedConstants.SEGMENT_COHORTS_CRITERIA_OPTIONS.ECOMMERCE_RECOMMENDED_EVENT,
} as const;
// Re-export as a type with the same name so existing `criteria_option?: CRITERIA_OPTIONS` usages continue to work.
export type CRITERIA_OPTIONS = (typeof CRITERIA_OPTIONS)[keyof typeof CRITERIA_OPTIONS];

export type PropertyFilterGroupKey = "or_behavior_condition_filter" | "and_behavior_condition_filter";

export interface PropertyFilterGroup {
  filter_key: PropertyFilterGroupKey;
  filters: PropertyFilter[];
}

export interface PropertyFilter {
  comparator: number;
  complexity: string;
  name: string;
  value: string[] | string | number | boolean;
  type: string;
}

export interface RefreshSchedule {
  sunday?: boolean;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  start_date?: string;
  start_hour?: Hour;
  start_minute?: Minute;
  frequency: RefreshFrequency;
}

export interface BaseSegmentCohort {
  name: string;
  recurring_schedule?: RefreshSchedule;
  recurring_refresh_enabled?: boolean;
  auto_disabled_at?: string;
  has_upcoming_auto_disable?: boolean;
  upcoming_refresh?: string;
  type?: SegmentCohortType;
  is_draft?: boolean;
}

export interface IncrementalSegmentCohort {
  count?: number;
  count_operator?: string;
  date_range_type?: SegmentCohortRangeType;
  y_days?: number;
  start_date?: string;
  end_date?: string;
  should_invert?: boolean;
}

type ValueOf<T> = T[keyof T];
export type SegmentCohortStatus = ValueOf<typeof sharedConstants.SEGMENT_COHORTS_DEFINITION_STATUS>;

export interface SegmentCohort extends BaseTableResultItem, BaseSegmentCohort, IncrementalSegmentCohort {
  custom_event_name?: string;
  product_name?: string;
  sql?: string;
  message_channel_interaction_name?: string;
  status?: SegmentCohortStatus;
  last_processed?: string;
  last_edited?: string;
  last_edited_by?: { id?: string; name?: string; email?: string };
  property_filters?: (PropertyFilterGroup | PropertyFilter)[];
  marked_as_deleted?: boolean;
  /** Used when segment_extensions_filter_new_data_format is ON */
  filters?: SegmentCohortFilter[];
  status_reason?: string;
  filter_on_type?: MessageChannelSpecificInteraction;
  filter_on_value?: string;
  filter_on_value_name?: string;
  filter_on_metadata?: string;
  variables?: PersistedVariable[];
  data_warehouse_config_id?: string;
  data_warehouse_config_partner_name?: string;
  full_refresh_enabled?: boolean;
  changelog_count?: number;
  /** Sent only while segment_extensions_tags is on. */
  tag_names?: string[];
}

export interface SegmentCohortFilter extends IncrementalSegmentCohort {
  id?: string;
  type?: CRITERIA_OPTIONS;
  custom_event_name?: string;
  product_name?: string | string[];
  message_channel_interaction_name?: string;
  property_filters?: (PropertyFilterGroup | PropertyFilter)[];
  filter_on_type?: MessageChannelSpecificInteraction;
  filter_on_value?: string;
  filter_on_value_name?: string;
  filter_on_metadata?: string;
}

export interface SegmentCohortsListParams {
  /** the query object containing all the filters */
  query?: Partial<Record<string, string | string[]>>;
  /** the column on which to sort */
  sortby?: string;
  /** the direction in which to sort */
  sortdir?: -1 | 1;
  /** the number of records to return per page */
  limit?: number;
  /** the index for the first record in the set among the entire response */
  start?: number;
  /** use the new "results" key instead of "segment_cohorts" for list data */
  datatable_format?: boolean;
}

export type SegmentCohortList = TableResult<SegmentCohort> & {
  segment_cohorts: SegmentCohort[] | null;
  max_braze_count?: number;
  active_braze_count?: number;
  mtd_sql_credits_used?: number;
  mtd_sql_credits_available?: number;
  max_cdi_count?: number;
  active_cdi_count?: number;
  auto_refresh_disabled_segment_cohorts_count?: number;
};

export interface PatchEngagementApiSegmentCohortsIdProps {
  segmentCohortId: string;
  segmentCohort: Partial<SegmentCohort>;
}

export type MessageChannelSpecificInteraction = "campaign_id" | "canvas_id" | "canvas_step_id";

export type SegmentCohortRangeType = "interval" | "y_days";
export type SegmentCohortRangeOption = "relative" | "date-range" | "start-date" | "end-date";

export type SegmentCohortSqlType = "CDI_SQL" | "BRAZE_SQL" | "INCREMENTAL_BRAZE_SQL";
export type SegmentCohortType = SegmentCohortSqlType | "UI" | "DATA_OBJECTS_FILTER";
export type CreateSegmentCohortSqlOption = SegmentCohortSqlType | "TEMPLATE";
